import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { LayoutComponent } from './layout/layout.component';
import { CommonModule } from '@angular/common';
import { LoginComponent } from './login/login.component';
import { SessionTimeoutService } from '../app/service/session-timeout.service';
import { ChangePasswordComponent } from './pages/change-password/change-password.component';
import { ErrorPageComponent } from './pages/ErrorPages/error-page/error-page.component';
import { PlausibleAnalyticsService } from './service/plausible-analytics.service';
import { UserService } from './service/user.service';
import { TokenRefreshService } from './service/token-refresh.service';
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterModule, LayoutComponent, LoginComponent, ChangePasswordComponent, ErrorPageComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})

export class AppComponent implements OnInit, OnDestroy {
  showContent = true;
  currentRoute?: string;

  // Proactively renew the access token every 4 min — comfortably inside its 5-min life.
  // This keeps the session alive during IDLE periods too (no user request needed), so the
  // token no longer silently expires at 5 min. It deliberately does NOT reset the 15-min
  // inactivity timer, so a truly idle user is still logged out at 15 min.
  private readonly proactiveRefreshMs = 4 * 60 * 1000;
  private proactiveRefreshTimer: any;

  constructor(private router: Router, private sessionTimeoutService: SessionTimeoutService, private plausible: PlausibleAnalyticsService, private userService: UserService, private tokenRefreshService: TokenRefreshService) { }

  ngOnInit(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = this.router.url;
        this.showContent = !(event.url === '/Login' || event.url === '/' || event.url === '/ForgetPassword' || event.url.startsWith('/Error'));
        const routeName = event.urlAfterRedirects.split('/').pop();
        const userRole = this.userService.getRole();
        if (routeName && userRole) {
          this.plausible.trackPageview(routeName, userRole);
        }
      }
    });

    this.startProactiveTokenRefresh();
  }

  private startProactiveTokenRefresh(): void {
    clearInterval(this.proactiveRefreshTimer);
    this.proactiveRefreshTimer = setInterval(() => {
      // Only while signed in. Best-effort: on failure the 401 interceptor is the safety net,
      // and once the 15-min inactivity logout clears storage this simply stops refreshing.
      if (this.userService.getRefreshToken()) {
        this.tokenRefreshService.refreshToken().subscribe({
          next: () => { },
          error: () => { }
        });
      }
    }, this.proactiveRefreshMs);
  }

  ngOnDestroy(): void {
    clearInterval(this.proactiveRefreshTimer);
  }

  @HostListener('document:mousemove')
  @HostListener('document:keydown')
  handleUserActivity() {
    this.sessionTimeoutService.resetTimer();
  }

  // NOTE — there is deliberately NO unload/pagehide handler that invalidates the JWT.
  //
  // A previous version called LogoutJTIUpdate on 'pagehide' so a captured token could not be
  // reused after the tab closed. The web platform cannot distinguish a CLOSE from a RELOAD at
  // unload time (only F5 / Ctrl+R are observable as keystrokes — the reload button and the
  // address bar are not), so every reload blacklisted the JTI the reloaded page was about to
  // keep using. The session then died on refresh, which is exactly what users reported.
  //
  // The token is safe without it: the access token lives only 5 minutes and is DPoP-bound
  // (Dpop:Enforce = true) to a NON-EXTRACTABLE key held in this browser profile's IndexedDB,
  // so a captured token cannot be replayed from anywhere else. An explicit sign-out still
  // revokes the JTI immediately — see LayoutComponent.logOut().
}
